/*
 * YourXI Bespoke XI: personalisation (title, club badge, shirt numbers, nicknames)
 * --------------------------------------------------------------------------
 * window.YXIPersonalise
 *
 * TITLE
 *   mountTitleEditor(el, {value, onChange}) -> {update, getValue, destroy}
 *   renderTitle(value, {cx, width, centerY, minTop, maxBottom, style, badge}) -> SVG string
 *   layoutTitle(value, opts) -> {svg, main:[lines], topSize, mainSize, top, bottom}
 *   value = { top: 'HAWTHORN LANE FC' (32 max, light), main: '2025 CUP WINNERS XI' (40 max, bold) }
 *
 *   Typography measured off the original prints (Everton 1985, Newcastle 2025, Barcelona 2011), 900-wide units:
 *     top line   Montserrat Light 300, 43 (cap 30), tracking -0.05em
 *     main title Montserrat ExtraBold 800, 58.5 (cap 41), tracking -0.01em, line pitch 54 (0.923 em)
 *     baselines  top 107, main 170 and 224  (block centred on y 150.5, the two-line print case exactly)
 *     block kept inside y 14-226 so it clears the front-row shirts (top ~230); with a badge it scales ~3%
 *     max line width 790 (Barcelona's widest line is 782)
 *   One main line when it fits at full size, otherwise two balanced lines; the last line never holds a
 *   single word when the title has 3+ words (no widows). Lines then shrink to fit (main down to 34,
 *   top down to 26). Everything is uppercase on the print.
 *
 * BADGE (own-team mode)
 *   mountBadgeEditor(el, {value, onChange}) -> {update, getValue, getOriginalFile(), destroy}
 *   renderBadge(value | dataUrl, {cx, y, size}) -> SVG <image> string
 *   value = { dataUrl: 'data:image/png;base64,...' (<= 200 KB, 256 px square, transparent),
 *             name: 'badge.png', removeWhite: false, kb: 38 }
 *   Upload PNG/JPG/SVG, square crop (drag to move, slider to zoom; zoom 1 = whole badge fits),
 *   optional "Remove white background" (flood fill from the edges, soft edge). The original File is
 *   kept in memory for the cart: controller.getOriginalFile().
 *   Placement (matching his centred layout): small, centred ABOVE the title. Pass the dataUrl as
 *   renderTitle(..., {badge}) and the title block makes room for it (badge 54 tall, 16 gap).
 *
 * SHIRT NUMBERS + NICKNAMES (per player)
 *   mountPlayerEditor(el, {value:{number, nickname}, onChange}) -> {update, getValue, destroy}
 *   numberSVG(number, kitParams, shirtBox, {onDark}) -> SVG string
 *       kitParams: kits.js kitSVG params (body, pattern layers, collar, cuff) or kitmaker custom opts.
 *       shirtBox:  {x, y, w, h} = the box the kit <svg> is nested into (xMidYMax meet), e.g. the wizard's
 *                  nest(svg, -SW/2, top, SW, SH) -> {x:-52, y:-82, w:104, h:127}.
 *       Colour: a kit colour (trim, collar, pattern) that contrasts with every chest colour, else black or
 *       white; striped kits where nothing contrasts get a thin outline, like real shirt numbers.
 *   numberColours(kitParams) -> {fill, stroke|null}
 *   renderNickname(text, {x, y, nameSize, style}) -> SVG string. y = nickname baseline.
 *   nicknameBaseline(surnameBaseline, nameSize) -> y for the nickname (0.62 x nameSize below)
 *   Nickname: 18 chars max, Montserrat Medium Italic at 0.5 x surname size, in quotes: ‘THE WALL’.
 *   With match annotations below it, call YXIMatch.renderPlayerAnnotations with
 *   nameBottom = nickname baseline and after:'nickname'.
 */
(function (root) {
  'use strict';

  var FONT = 'Montserrat';
  var LIMITS = { top: 32, main: 40, nickname: 18, number: 2, badgeKB: 200 };
  var T = { topSize: 43, topTrack: -0.05, mainSize: 58.5, mainTrack: -0.01, pitch: 0.923, topToMain: 63, width: 790, centerY: 150.5, minTop: 14, maxBottom: 226, badge: 54, badgeGap: 16 };
  var uid = 0;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function r1(n) { return Math.round(n * 100) / 100; }
  function clone(o) { return JSON.parse(JSON.stringify(o)); }
  function clean(s) { return String(s == null ? '' : s).replace(/[–—]/g, '-').replace(/\s+/g, ' ').replace(/^\s+/, ''); }

  /* ---------------- colour ---------------- */
  function hexToRgb(h) {
    h = String(h || '#000').replace('#', '');
    if (h.length === 3) h = h.split('').map(function (c) { return c + c; }).join('');
    var n = parseInt(h, 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function lum(c) {
    var r = hexToRgb(c).map(function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
    return 0.2126 * r[0] + 0.7152 * r[1] + 0.0722 * r[2];
  }
  function contrast(a, b) { var A = lum(a), B = lum(b); return (Math.max(A, B) + 0.05) / (Math.min(A, B) + 0.05); }
  function isHex(c) { return typeof c === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(c); }
  function same(a, b) { return String(a).toLowerCase() === String(b).toLowerCase(); }

  var FALLBACK_STYLES = {
    classic: { paper: '#FFFFFF', textPrimary: '#111111', textSecondary: '#62625F', halo: '#FFFFFF' },
    night: { paper: '#141414', textPrimary: '#F5F3EE', textSecondary: '#A9A7A1', halo: '#141414' }
  };
  function resolveStyle(style) {
    var st;
    if (style && typeof style === 'object') st = style;
    else if (root.YourXIPitches && root.YourXIPitches.getStyle) st = root.YourXIPitches.getStyle(style || 'classic');
    else st = FALLBACK_STYLES[style] || FALLBACK_STYLES.classic;
    var paper = st.paper || st.bg || '#FFFFFF';
    return { ink: st.textPrimary || st.ink || '#111111', soft: st.textSecondary || st.muted || '#62625F', halo: st.halo || paper, paper: paper, dark: lum(paper) < 0.2 };
  }

  /* ---------------- measuring ---------------- */
  var ctx = null;
  function measure(t, size, weight, trackEm, italic) {
    if (!ctx && typeof document !== 'undefined') { try { ctx = document.createElement('canvas').getContext('2d'); } catch (e) { ctx = null; } }
    var w;
    if (ctx) { ctx.font = (italic ? 'italic ' : '') + weight + ' ' + size + 'px ' + FONT + ', sans-serif'; w = ctx.measureText(t).width; }
    else w = t.length * size * (weight >= 700 ? 0.7 : 0.62);
    return w + Math.max(0, t.length - 1) * (trackEm || 0) * size;
  }

  /* ---------------- title ---------------- */
  function normalizeTitle(v) {
    v = v || {};
    return { top: clean(v.top).slice(0, LIMITS.top), main: clean(v.main).slice(0, LIMITS.main) };
  }
  // best two-line split: smallest widest line, never a lone word on the last line (3+ words),
  // ties go to the longer first line (as "2025 CARABAO CUP / CHAMPIONS XI")
  function splitMain(text, size) {
    var words = text.split(' ').filter(Boolean);
    if (words.length < 2) return [text];
    var best = null;
    for (var i = 1; i < words.length; i++) {
      var a = words.slice(0, i).join(' '), b = words.slice(i).join(' ');
      if (words.length >= 3 && words.length - i < 2) continue;
      var wa = measure(a, size, 800, T.mainTrack), wb = measure(b, size, 800, T.mainTrack), m = Math.max(wa, wb);
      if (!best || m < best.m - 0.5 || (Math.abs(m - best.m) <= 0.5 && wa > best.wa)) best = { lines: [a, b], m: m, wa: wa };
    }
    return best ? best.lines : [text];
  }
  function layoutTitle(value, opts) {
    opts = opts || {};
    if (opts.tokens && !opts._tok) { var saved = T; T = Object.assign({}, T, opts.tokens); try { return layoutTitle(value, Object.assign({}, opts, { _tok: 1, centerY: opts.centerY != null ? opts.centerY : T.centerY, minTop: T.minTop })); } finally { T = saved; } }
    var v = normalizeTitle(value), S = resolveStyle(opts.style);
    var cx = opts.cx != null ? +opts.cx : 450, W = +opts.width || T.width;
    var top = v.top.toUpperCase(), main = (v.main || opts.fallback || 'YOUR ALL TIME XI').toUpperCase();
    var ts0 = T.topSize, ms0 = T.mainSize;
    if (top) { var tw = measure(top, ts0, T.topWeight || 300, T.topTrack); if (tw > W) ts0 = Math.max(26, ts0 * W / tw); }
    var lines = measure(main, ms0, 800, T.mainTrack) <= W ? [main] : splitMain(main, ms0);
    var mw = Math.max.apply(null, lines.map(function (l) { return measure(l, ms0, 800, T.mainTrack); }));
    if (mw > W) ms0 = Math.max(34, ms0 * W / mw);
    var minTop = opts.minTop != null ? +opts.minTop : T.minTop, maxBottom = opts.maxBottom != null ? +opts.maxBottom : T.maxBottom;
    var ts, ms, badge, parts, H;
    // stack from the first item's top edge; if it is taller than the space, scale the whole block a little
    for (var f = 1; f >= 0.7; f -= 0.01) {
      ts = ts0 * f; ms = ms0 * f; badge = opts.badge ? T.badge * Math.max(f, 0.8) : 0;
      var pitch = ms * T.pitch, y = 0; parts = [];
      if (badge) { parts.push({ k: 'badge', y: 0 }); y = badge + T.badgeGap * f; }
      if (top) { y += ts * 0.7; parts.push({ k: 'top', base: y }); y += (T.topToMain - T.mainSize * 0.7) * (ms / T.mainSize) + ms * 0.7; }   // 22 gap, then main cap
      else y += ms * 0.7;
      lines.forEach(function (l, i) { if (i) y += pitch; parts.push({ k: 'main', base: y, t: l }); });
      H = y;
      if (H <= maxBottom - minTop) break;
    }
    var c = opts.centerY != null ? +opts.centerY : T.centerY;
    var y0 = c - H / 2;
    if (y0 + H > maxBottom) y0 = maxBottom - H;
    if (y0 < minTop) y0 = minTop;
    var svg = '';
    parts.forEach(function (p) {
      if (p.k === 'badge') svg += renderBadge(opts.badge, { cx: cx, y: y0, size: badge });
      else if (p.k === 'top') svg += '<text x="' + r1(cx) + '" y="' + r1(y0 + p.base) + '" text-anchor="middle" font-family="' + FONT + ', sans-serif" font-size="' + r1(ts) + '" font-weight="' + (T.topWeight || 300) + '" letter-spacing="' + r1(T.topTrack * ts) + '" fill="' + S.ink + '">' + esc(top) + '</text>';
      else svg += '<text x="' + r1(cx) + '" y="' + r1(y0 + p.base) + '" text-anchor="middle" font-family="' + FONT + ', sans-serif" font-size="' + r1(ms) + '" font-weight="800" letter-spacing="' + r1(T.mainTrack * ms) + '" fill="' + S.ink + '">' + esc(p.t) + '</text>';
    });
    return { svg: '<g class="yxp-title">' + svg + '</g>', main: lines, topSize: ts, mainSize: ms, top: y0, bottom: y0 + H };
  }
  function renderTitle(value, opts) { return layoutTitle(value, opts).svg; }

  function counterHTML(n, max) { return '<span class="yxp-count' + (n >= max ? ' is-full' : '') + '">' + n + '/' + max + '</span>'; }
  function bindCount(el, input, max) { var c = input.closest('.yxp-field').querySelector('.yxp-count'); if (c) { c.textContent = input.value.length + '/' + max; c.classList.toggle('is-full', input.value.length >= max); } }

  function mountTitleEditor(el, opts) {
    opts = opts || {};
    var state = normalizeTitle(opts.value), onChange = typeof opts.onChange === 'function' ? opts.onChange : function () {}, dead = false, id = 'yxpt' + (++uid);
    el.innerHTML = '<div class="yxp">' +
      '<div class="yxp-field"><div class="yxp-row"><label class="yxp-label" for="' + id + 'a">Top line <span class="yxp-opt">light, smaller</span></label>' + counterHTML(state.top.length, LIMITS.top) + '</div>' +
      '<input class="yxp-input" id="' + id + 'a" data-f="top" maxlength="' + LIMITS.top + '" autocomplete="off" placeholder="e.g. Hawthorn Lane FC" value="' + esc(state.top) + '"></div>' +
      '<div class="yxp-field"><div class="yxp-row"><label class="yxp-label" for="' + id + 'b">Title <span class="yxp-opt">bold, up to two lines</span></label>' + counterHTML(state.main.length, LIMITS.main) + '</div>' +
      '<input class="yxp-input" id="' + id + 'b" data-f="main" maxlength="' + LIMITS.main + '" autocomplete="off" placeholder="e.g. 2025 Cup Winners XI" value="' + esc(state.main) + '">' +
      '<p class="yxp-hint">Printed in capitals. Long titles split over two balanced lines.</p></div></div>';
    function onInput(e) {
      var t = e.target, f = t.dataset && t.dataset.f; if (!f) return;
      var v = clean(t.value).slice(0, LIMITS[f]); if (v !== t.value.replace(/^\s+/, '')) t.value = v;
      state[f] = v; bindCount(el, t, LIMITS[f]); if (!dead) onChange(clone(state));
    }
    el.addEventListener('input', onInput);
    return {
      update: function (v) { var n = normalizeTitle(v); state = n; ['top', 'main'].forEach(function (f) { var i = el.querySelector('[data-f="' + f + '"]'); if (i && i.value !== n[f]) { i.value = n[f]; bindCount(el, i, LIMITS[f]); } }); },
      getValue: function () { return clone(state); },
      destroy: function () { dead = true; el.removeEventListener('input', onInput); el.innerHTML = ''; }
    };
  }

  /* ---------------- badge ---------------- */
  function badgeUrl(v) { return typeof v === 'string' ? v : v && v.dataUrl ? v.dataUrl : ''; }
  function renderBadge(value, opts) {
    var url = badgeUrl(value); if (!url) return '';
    opts = opts || {};
    var size = +opts.size || T.badge, cx = opts.cx != null ? +opts.cx : 450, y = +opts.y || 0;
    return '<image class="yxp-badge" href="' + esc(url) + '" x="' + r1(cx - size / 2) + '" y="' + r1(y) + '" width="' + r1(size) + '" height="' + r1(size) + '" preserveAspectRatio="xMidYMid meet"/>';
  }

  var OUT = 256;
  function loadImage(file) {
    return new Promise(function (res, rej) {
      var url = URL.createObjectURL(file), img = new Image();
      img.onload = function () { res({ img: img, url: url, w: img.naturalWidth || 512, h: img.naturalHeight || 512 }); };
      img.onerror = function () { URL.revokeObjectURL(url); rej(new Error('Could not read that image')); };
      img.src = url;
    });
  }
  function removeWhite(data, W, H) {
    var d = data.data, n = W * H, seen = new Uint8Array(n), q = new Int32Array(n), qh = 0, qt = 0;
    function bg(i) { var p = i * 4, a = d[p + 3]; if (a < 16) return true; var mn = Math.min(d[p], d[p + 1], d[p + 2]), mx = Math.max(d[p], d[p + 1], d[p + 2]); return mn >= 228 && mx - mn <= 22; }
    function push(i) { if (!seen[i] && bg(i)) { seen[i] = 1; q[qt++] = i; } }
    for (var x = 0; x < W; x++) { push(x); push((H - 1) * W + x); }
    for (var y = 0; y < H; y++) { push(y * W); push(y * W + W - 1); }
    while (qh < qt) {
      var i = q[qh++], cx = i % W, cy = (i / W) | 0;
      if (cx > 0) push(i - 1); if (cx < W - 1) push(i + 1); if (cy > 0) push(i - W); if (cy < H - 1) push(i + W);
    }
    for (var k = 0; k < n; k++) if (seen[k]) d[k * 4 + 3] = 0;
    // soft edge: light pixels touching the removed area fade with their lightness
    for (var j = 0; j < n; j++) {
      if (seen[j]) continue;
      var jx = j % W, jy = (j / W) | 0;
      var edge = (jx > 0 && seen[j - 1]) || (jx < W - 1 && seen[j + 1]) || (jy > 0 && seen[j - W]) || (jy < H - 1 && seen[j + W]);
      if (!edge) continue;
      var p = j * 4, mn = Math.min(d[p], d[p + 1], d[p + 2]);
      if (mn > 170) d[p + 3] = Math.round(d[p + 3] * Math.max(0, Math.min(1, (255 - mn) / 85)));
    }
    return data;
  }
  function renderCanvas(src, view, rmWhite, size) {
    var c = document.createElement('canvas'); c.width = c.height = size;
    var g = c.getContext('2d'), s0 = size / Math.max(src.w, src.h), s = s0 * view.zoom;
    g.imageSmoothingQuality = 'high';
    g.drawImage(src.img, size / 2 + view.ox * size - src.w * s / 2, size / 2 + view.oy * size - src.h * s / 2, src.w * s, src.h * s);
    if (rmWhite) { var id = g.getImageData(0, 0, size, size); g.putImageData(removeWhite(id, size, size), 0, 0); }
    return c;
  }
  function toSmallDataUrl(src, view, rmWhite) {
    var sizes = [256, 224, 192, 160, 128];
    for (var i = 0; i < sizes.length; i++) {
      var c = renderCanvas(src, view, rmWhite, sizes[i]), url = c.toDataURL('image/png');
      if (url.length * 0.75 <= LIMITS.badgeKB * 1024) return url;
      var webp = c.toDataURL('image/webp', 0.9);
      if (webp.indexOf('data:image/webp') === 0 && webp.length * 0.75 <= LIMITS.badgeKB * 1024) return webp;
    }
    return renderCanvas(src, view, rmWhite, 96).toDataURL('image/png');
  }

  function mountBadgeEditor(el, opts) {
    opts = opts || {};
    var value = opts.value && opts.value.dataUrl ? clone(opts.value) : null;
    var onChange = typeof opts.onChange === 'function' ? opts.onChange : function () {};
    var src = null, file = null, view = { zoom: 1, ox: 0, oy: 0 }, rm = !!(value && value.removeWhite), dead = false, id = 'yxpb' + (++uid), err = '';
    function emit() { if (!dead) onChange(value ? clone(value) : null); }
    function commit() {
      if (!src) return;
      var url = toSmallDataUrl(src, view, rm);
      value = { dataUrl: url, name: file ? file.name : (value && value.name) || 'badge.png', removeWhite: rm, kb: Math.round(url.length * 0.75 / 1024) };
      var p = el.querySelector('.yxp-bout img'); if (p) p.src = url;
      var k = el.querySelector('.yxp-kb'); if (k) k.textContent = value.kb + ' KB';
      emit();
    }
    function drawCrop() {
      var cv = el.querySelector('.yxp-crop canvas'); if (!cv || !src) return;
      var size = cv.width, c = renderCanvas(src, view, rm, size), g = cv.getContext('2d');
      g.clearRect(0, 0, size, size); g.drawImage(c, 0, 0);
    }
    function render() {
      if (!value && !src) {
        el.innerHTML = '<div class="yxp yxp-b">' +
          '<label class="yxp-drop" for="' + id + 'f"><span class="yxp-drop-ic" aria-hidden="true"></span><span><b>Upload your club badge</b><small>PNG, JPG or SVG. It prints small, centred above the title.</small></span></label>' +
          '<input class="yxp-file" type="file" id="' + id + 'f" accept="image/png,image/jpeg,image/svg+xml,.svg">' +
          (err ? '<p class="yxp-err" role="alert">' + esc(err) + '</p>' : '') + '</div>';
        return;
      }
      el.innerHTML = '<div class="yxp yxp-b">' +
        (src ? '<div class="yxp-cropwrap"><div class="yxp-crop" aria-label="Badge crop. Drag to move."><canvas width="480" height="480"></canvas><i class="yxp-safe" aria-hidden="true"></i></div>' +
          '<div class="yxp-zoom"><label for="' + id + 'z">Zoom</label><input type="range" id="' + id + 'z" min="1" max="4" step="0.01" value="' + view.zoom + '" data-z="1"></div></div>'
          : '<div class="yxp-bout yxp-bout-only"><img alt="Your badge" src="' + esc(value.dataUrl) + '"></div><p class="yxp-hint">Upload the badge again to change the crop.</p>') +
        '<div class="yxp-toggle"><span id="' + id + 'w">Remove white background</span><button type="button" class="yxp-switch" role="switch" aria-labelledby="' + id + 'w" aria-checked="' + rm + '" data-rm="1"' + (src ? '' : ' disabled') + '></button></div>' +
        (src ? '<div class="yxp-brow"><span class="yxp-bout"><img alt="Badge as it will print" src="' + esc(value ? value.dataUrl : '') + '"></span><span class="yxp-bmeta"><b>' + esc(file ? file.name : value.name) + '</b><small class="yxp-kb">' + (value ? value.kb + ' KB' : '') + '</small></span></div>' : '') +
        '<div class="yxp-btns"><label class="yxp-btn ghost" for="' + id + 'f">Replace</label><button type="button" class="yxp-btn ghost" data-clear="1">Remove badge</button></div>' +
        '<input class="yxp-file" type="file" id="' + id + 'f" accept="image/png,image/jpeg,image/svg+xml,.svg">' +
        (err ? '<p class="yxp-err" role="alert">' + esc(err) + '</p>' : '') + '</div>';
      drawCrop();
      bindDrag();
    }
    function bindDrag() {
      var box = el.querySelector('.yxp-crop'); if (!box) return;
      var start = null;
      box.addEventListener('pointerdown', function (e) { start = { x: e.clientX, y: e.clientY, ox: view.ox, oy: view.oy, w: box.clientWidth }; box.setPointerCapture(e.pointerId); });
      box.addEventListener('pointermove', function (e) {
        if (!start) return;
        view.ox = Math.max(-0.5, Math.min(0.5, start.ox + (e.clientX - start.x) / start.w));
        view.oy = Math.max(-0.5, Math.min(0.5, start.oy + (e.clientY - start.y) / start.w));
        drawCrop();
      });
      function end() { if (start) { start = null; commit(); } }
      box.addEventListener('pointerup', end); box.addEventListener('pointercancel', end);
      box.tabIndex = 0;
      box.addEventListener('keydown', function (e) {
        var d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key]; if (!d) return;
        e.preventDefault(); view.ox = Math.max(-0.5, Math.min(0.5, view.ox + d[0] * 0.01)); view.oy = Math.max(-0.5, Math.min(0.5, view.oy + d[1] * 0.01)); drawCrop(); commit();
      });
    }
    function onChangeEv(e) {
      var t = e.target;
      if (t.classList && t.classList.contains('yxp-file') && t.files && t.files[0]) {
        var f = t.files[0];
        if (!/^image\/(png|jpeg|svg\+xml)$/.test(f.type) && !/\.svg$/i.test(f.name)) { err = 'Please choose a PNG, JPG or SVG file.'; render(); return; }
        if (f.size > 15 * 1024 * 1024) { err = 'That file is over 15 MB. Try a smaller copy.'; render(); return; }
        err = '';
        loadImage(f).then(function (s) {
          if (src && src.url) URL.revokeObjectURL(src.url);
          src = s; file = f; view = { zoom: 1, ox: 0, oy: 0 };
          // a photo or JPG on a white square: switch the white removal on by default
          rm = /jpeg/.test(f.type) || rm;
          value = null; render(); commit();
        }).catch(function (x) { err = x.message; render(); });
      }
      if (t.dataset && t.dataset.z) commit();
    }
    function onInput(e) { if (e.target.dataset && e.target.dataset.z) { view.zoom = +e.target.value; drawCrop(); } }
    function onClick(e) {
      var b = e.target.closest && e.target.closest('button'); if (!b || !el.contains(b)) return;
      if (b.dataset.rm) { rm = !rm; b.setAttribute('aria-checked', rm); drawCrop(); commit(); }
      if (b.dataset.clear) { if (src && src.url) URL.revokeObjectURL(src.url); src = null; file = null; value = null; render(); emit(); }
    }
    el.addEventListener('change', onChangeEv); el.addEventListener('input', onInput); el.addEventListener('click', onClick);
    render();
    return {
      update: function (v) { if (JSON.stringify(v || null) === JSON.stringify(value)) return; value = v && v.dataUrl ? clone(v) : null; if (!value) { src = null; file = null; } rm = !!(value && value.removeWhite); render(); },
      getValue: function () { return value ? clone(value) : null; },
      getOriginalFile: function () { return file; },
      loadFile: function (f) { var dt = { target: { classList: { contains: function () { return true; } }, files: [f], dataset: {} } }; onChangeEv(dt); },
      destroy: function () { dead = true; if (src && src.url) URL.revokeObjectURL(src.url); el.removeEventListener('change', onChangeEv); el.removeEventListener('input', onInput); el.removeEventListener('click', onClick); el.innerHTML = ''; }
    };
  }

  /* ---------------- shirt numbers ---------------- */
  // colours that sit on the chest, by pattern layer
  var CHEST = { stripes: 'colors', hoops: 'colors', gradient: 'colors', halves: 'color', quarters: 'color', sash: 'color', chevron: 'color', checks: 'color', dots: 'color', band: 'color' };
  function toKitParams(k) {
    if (!k) return { body: '#FFFFFF' };
    if (typeof k.pattern === 'string' && root.YourXIKits && root.YourXIKits.customKitParams) return root.YourXIKits.customKitParams(k);
    if (typeof k.pattern === 'string') return { body: k.body, pattern: k.pattern === 'plain' ? [] : [{ type: k.pattern, color: k.patternColor, colors: [k.body, k.patternColor] }], collar: { color: k.collar } };
    return k;
  }
  function numberColours(kit) {
    var P = toKitParams(kit), body = isHex(P.body) ? P.body : '#FFFFFF', chest = [body], accents = [];
    (P.pattern || []).forEach(function (L) {
      var key = CHEST[L.type];
      if (key === 'colors') (L.colors || []).forEach(function (c) { if (isHex(c)) chest.push(c); });
      else if (key && isHex(L.color)) chest.push(L.color);
      else if (isHex(L.color)) accents.push(L.color);
      (L.colors || []).forEach(function (c) { if (isHex(c)) accents.push(c); });
    });
    if (P.collar) { if (isHex(P.collar.color)) accents.push(P.collar.color); if (isHex(P.collar.trim)) accents.push(P.collar.trim); }
    if (P.cuff) { if (isHex(P.cuff.color)) accents.push(P.cuff.color); if (isHex(P.cuff.trim)) accents.push(P.cuff.trim); }
    if (isHex(P.sleeves)) accents.push(P.sleeves);
    function minC(c) { return Math.min.apply(null, chest.map(function (x) { return contrast(c, x); })); }
    var best = null;
    accents.filter(function (c) { return !chest.some(function (x) { return same(x, c); }); }).forEach(function (c) {
      var m = minC(c); if (m >= 3 && (!best || m > best.m)) best = { c: c, m: m };
    });
    if (!best) ['#FFFFFF', '#111111'].forEach(function (c) { var m = minC(c); if (!best || m > best.m) best = { c: c, m: m }; });
    // hoops and stripes: always outline the number in the opposite tone so it reads on every band
    if (best.m >= 2.2) return { fill: best.c, stroke: chest.length > 1 ? (contrast(best.c, '#111111') > contrast(best.c, '#FFFFFF') ? '#111111' : '#FFFFFF') : null };
    // nothing reads on every stripe: contrast with the body, outline in the opposite tone
    var fill = contrast('#FFFFFF', body) >= contrast('#111111', body) ? '#FFFFFF' : '#111111';
    return { fill: fill, stroke: fill === '#FFFFFF' ? '#111111' : '#FFFFFF' };
  }
  function numberSVG(number, kitParams, shirtBox, opts) {
    var n = String(number == null ? '' : number).replace(/\D/g, '').slice(0, 2);
    if (!n) return '';
    opts = opts || {};
    var b = shirtBox || { x: 0, y: 0, w: 178, h: 218 };
    var VW = 178, VH = 218, sc = Math.min(b.w / VW, b.h / VH);
    var x0 = b.x + (b.w - VW * sc) / 2, y0 = b.y + b.h - VH * sc;
    function px(kx) { return x0 + (kx + 4) * sc; }
    function py(ky) { return y0 + (ky + 4) * sc; }
    var cap = (n.length > 1 ? 40 : 44) * sc, size = cap / 0.7, col = numberColours(kitParams);
    var st = col.stroke ? ' stroke="' + col.stroke + '" stroke-width="' + r1(size * 0.09) + '" stroke-linejoin="round" paint-order="stroke"' : '';
    return '<text class="yxp-num" x="' + r1(px(85.08)) + '" y="' + r1(py(102) + cap / 2) + '" text-anchor="middle" font-family="' + FONT + ', sans-serif" font-size="' + r1(size) +
      '" font-weight="700" letter-spacing="' + r1(-size * 0.02) + '" fill="' + col.fill + '"' + st + '>' + n + '</text>';
  }

  /* ---------------- nicknames ---------------- */
  function normalizeNickname(s) { return clean(s).replace(/['‘’"“”]/g, '').slice(0, LIMITS.nickname); }
  function nicknameBaseline(surnameBaseline, nameSize) { return +surnameBaseline + (+nameSize || 30) * 0.62; }
  function renderNickname(text, opts) {
    var t = normalizeNickname(text).trim().toUpperCase(); if (!t) return '';
    opts = opts || {};
    var S = resolveStyle(opts.style), ls = +opts.nameSize || 30, size = ls * 0.5, maxW = +opts.maxWidth || 190;
    var label = '‘' + t + '’', w = measure(label, size, 500, 0.04, true);
    if (w > maxW) size *= maxW / w;
    return '<text class="yxp-nick" x="' + r1(+opts.x || 0) + '" y="' + r1(+opts.y || 0) + '" text-anchor="middle" font-family="' + FONT + ', sans-serif" font-style="italic" font-size="' + r1(size) +
      '" font-weight="500" letter-spacing="' + r1(size * 0.04) + '" fill="' + S.ink + '" stroke="' + S.halo + '" stroke-width="' + r1(size * 0.36) + '" stroke-linejoin="round" paint-order="stroke">' + esc(label) + '</text>';
  }

  /* ---------------- player editor: number + nickname ---------------- */
  function normalizePlayer(v) { v = v || {}; var n = String(v.number == null ? '' : v.number).replace(/\D/g, '').slice(0, 2); return { number: n === '0' || n === '00' ? '' : n.replace(/^0+(?=\d)/, ''), nickname: normalizeNickname(v.nickname) }; }
  function mountPlayerEditor(el, opts) {
    opts = opts || {};
    var state = normalizePlayer(opts.value), onChange = typeof opts.onChange === 'function' ? opts.onChange : function () {}, dead = false, id = 'yxpp' + (++uid);
    el.innerHTML = '<div class="yxp yxp-pl"><div class="yxp-pgrid">' +
      '<div class="yxp-field"><div class="yxp-row"><label class="yxp-label" for="' + id + 'n">Number</label></div>' +
      '<input class="yxp-input yxp-numin" id="' + id + 'n" data-f="number" inputmode="numeric" maxlength="2" autocomplete="off" placeholder="1-99" value="' + esc(state.number) + '"></div>' +
      '<div class="yxp-field"><div class="yxp-row"><label class="yxp-label" for="' + id + 'k">Nickname</label>' + counterHTML(state.nickname.length, LIMITS.nickname) + '</div>' +
      '<input class="yxp-input" id="' + id + 'k" data-f="nickname" maxlength="' + LIMITS.nickname + '" autocomplete="off" placeholder="e.g. The Wall" value="' + esc(state.nickname) + '"></div>' +
      '</div><p class="yxp-hint">Both optional. The number goes on the shirt chest, the nickname prints in small italics under the surname.</p></div>';
    function onInput(e) {
      var t = e.target, f = t.dataset && t.dataset.f; if (!f) return;
      if (f === 'number') { var d = t.value.replace(/\D/g, '').slice(0, 2); if (d !== t.value) t.value = d; }
      else { var c = normalizeNickname(t.value); if (c !== t.value) t.value = c; bindCount(el, t, LIMITS.nickname); }
      var n = normalizePlayer({ number: el.querySelector('[data-f="number"]').value, nickname: el.querySelector('[data-f="nickname"]').value });
      if (JSON.stringify(n) === JSON.stringify(state)) return;
      state = n; if (!dead) onChange(clone(state));
    }
    el.addEventListener('input', onInput);
    return {
      update: function (v) { var n = normalizePlayer(v); state = n; var a = el.querySelector('[data-f="number"]'), b = el.querySelector('[data-f="nickname"]'); if (a.value !== n.number) a.value = n.number; if (b.value !== n.nickname) { b.value = n.nickname; bindCount(el, b, LIMITS.nickname); } },
      getValue: function () { return clone(state); },
      destroy: function () { dead = true; el.removeEventListener('input', onInput); el.innerHTML = ''; }
    };
  }

  var api = {
    mountTitleEditor: mountTitleEditor, renderTitle: renderTitle, layoutTitle: layoutTitle, normalizeTitle: normalizeTitle,
    mountBadgeEditor: mountBadgeEditor, renderBadge: renderBadge,
    numberSVG: numberSVG, numberColours: numberColours,
    renderNickname: renderNickname, nicknameBaseline: nicknameBaseline,
    mountPlayerEditor: mountPlayerEditor, normalizePlayer: normalizePlayer,
    LIMITS: LIMITS, TYPE: T, _removeWhite: removeWhite
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.YXIPersonalise = api;
})(this);
